import { useEffect, useRef, useState } from "react";
import { Settings2 } from "lucide-react";
import { FeedbackToggle } from "./FeedbackToggle";
import { DuoSkinToggle } from "./DuoSkinToggle";

/**
 * Cog-menu popover hosting the in-session preferences:
 *   - Feedback Aan/Uit (always visible)
 *   - DUO-simulatie Aan/Uit (only when wired by the parent)
 */
export type SettingsPopoverProps = {
  feedbackEnabled: boolean;
  onChange: (enabled: boolean) => void;
  duoEnabled?: boolean;
  duoToggleDisabled?: boolean;
  onRequestDuoToggle?: (next: boolean) => void;
};

export function SettingsPopover({
  feedbackEnabled,
  onChange,
  duoEnabled,
  duoToggleDisabled = false,
  onRequestDuoToggle,
}: SettingsPopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const showDuoRow =
    duoEnabled !== undefined && onRequestDuoToggle !== undefined;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Instellingen"
        aria-expanded={open}
        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[var(--ink-muted)] hover:text-[var(--ink)] hover:bg-[var(--panel)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2 transition-colors"
      >
        <Settings2 size={18} aria-hidden />
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Instellingen"
          className="absolute right-0 mt-2 w-64 rounded-lg border border-[var(--border)] bg-[var(--panel)] p-3 shadow-lg z-20 space-y-3"
        >
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)]">
              Feedback
            </span>
            <FeedbackToggle feedbackEnabled={feedbackEnabled} onChange={onChange} />
          </div>
          {showDuoRow ? (
            <div className="space-y-1 border-t border-[var(--border)] pt-3">
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono-badge text-[10px] uppercase tracking-wider text-[var(--ink-faint)]">
                  DUO-simulatie
                </span>
                <DuoSkinToggle
                  duoEnabled={duoEnabled === true}
                  disabled={duoToggleDisabled}
                  onChange={onRequestDuoToggle}
                />
              </div>
              {duoToggleDisabled ? (
                <p className="text-[10px] text-[var(--ink-faint)]">
                  Alleen op desktop
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
